/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Params, Router} from "@angular/router";
import {RoleService, RoleWithRightsDto, RoleWithRightsVo, UsersService} from "@irian-ro/auth-web-client";
import { forkJoin, of, Subject } from 'rxjs';
import {switchMap, takeUntil} from "rxjs/operators";
import {UserContext} from "@irian-ro/auth-shared-module";
import {getSelectItemsAdminRights} from "@well/rights";
import {RoleConfigurationService} from "./role-configuration.service";
import {RoleCompanyTypeMeta} from "@well/domain";
import {JsonApiResource} from "@irian-ro/abstract-resource";
import {SelectItem} from "primeng/api";
import {TranslateService} from "@ngx-translate/core";
import { CompanyType } from '@well/common-libs/legal-entity-management/domain-model';

@Component({
  selector: 'well-role-configuration-page',
  templateUrl: './role-configuration-page.component.html',
  styleUrls: ['./role-configuration-page.component.scss'],
  providers: [RoleConfigurationService]
})
export class RoleConfigurationPageComponent implements OnInit, OnDestroy {
  constructor(private route: ActivatedRoute,
              private roleService: RoleService,
              private roleConfigurationService: RoleConfigurationService,
              private userService: UsersService,
              private router: Router,
              private userContext: UserContext,
              private translateService: TranslateService) {
  }

  assignedRightsMap: any = {};
  operation: string | undefined = undefined;
  rightNames: string[] = [];
  editedRole: RoleWithRightsDto | undefined = undefined;
  roleWithRightVOs: RoleWithRightsVo[] | undefined = undefined;
  destroy$: Subject<void> = new Subject<void>();
  hasSelectItemsAdminRights: boolean | undefined = undefined;

  editMode = false;
  meta: RoleCompanyTypeMeta | null = null;
  companyTypesSelectItems: SelectItem[] = [];

  roleFetchSubject:Subject<any> = new Subject<any>();

  showSavedDialog = false;

  companyTypeTranslationMap: {[s: string]: string} = {};

  ngOnInit() {

    this.hasSelectItemsAdminRights = this.userContext.hasAnyRights(getSelectItemsAdminRights());

    this.initCompanyTypeTranslationMap();

    this.listenToRoleFetchSubject();

    this.route.params.pipe(
      takeUntil(this.destroy$)
    )
      .subscribe((params: Params) => {
        console.log(params);
        const operation = params['op'];
        const roleUuid = params['uuid'];
        this.roleFetchSubject.next({operation: operation, roleUuid: roleUuid});
      })

  }

  private initCompanyTypeTranslationMap(){

    const companyTypeTranslationPrefix = 'company.companyType'

    const companyTypes = Object.keys(CompanyType);

    const companyTypesTranslationKeys = companyTypes.map(companyType => `${companyTypeTranslationPrefix}.${companyType}`);

    this.translateService.stream(companyTypesTranslationKeys).subscribe((res) => {
      for(const companyType of companyTypes){
        this.companyTypeTranslationMap[companyType] = res[`${companyTypeTranslationPrefix}.${companyType}`];
      }
      this.setCompanyTypeSelectItems();
    })
  }

  setEditMode(){
    this.editMode = this.operation === 'edit';
  }

  listenToRoleFetchSubject(){
    this.roleFetchSubject.pipe(
      takeUntil(this.destroy$),
      switchMap((routeParams: {operation: string, roleUuid: string}) => {
        return forkJoin([of(routeParams.operation), of(routeParams.roleUuid), this.roleConfigurationService.getEditedRoleDto(routeParams.operation, routeParams.roleUuid),
          this.roleConfigurationService.getRolesForCurrentOwnerByCompanyType()])
      })
    ).subscribe((result: [string, string, JsonApiResource<RoleWithRightsDto, RoleCompanyTypeMeta>, RoleWithRightsVo[]]) => {
      this.editedRole = result[2].data;
      this.editedRole.platformRole = this.hasSelectItemsAdminRights;
      this.meta = result[2].meta;
      this.setCompanyTypeSelectItems();
      this.rightNames = this.editedRole.roleRights?.map((roleRightDefintionDto: any) => roleRightDefintionDto.rightName) ?? [];
      this.roleWithRightVOs = result[3];
      this.sortRolesPlatformFirst();
      this.operation = result[0];
      this.setEditMode();

      if(this.operation == 'edit'){
        const roleUuid = result[1];
        this.roleWithRightVOs = this.roleWithRightVOs.filter(roleWithRightsVo => roleWithRightsVo.uuid != roleUuid);
      }

      this.assignedRightsMap = this.roleService.getAssignedRightsMap(this.rightNames, this.roleWithRightVOs);
    })
  }

  sortRolesPlatformFirst(){
    this.roleWithRightVOs = this.roleWithRightVOs?.sort((roleWithRightsVO1: RoleWithRightsVo, roleWithRightsVO2: RoleWithRightsVo) => {
        if(roleWithRightsVO1.platformRole == roleWithRightsVO2.platformRole){
          if(roleWithRightsVO1.name == roleWithRightsVO2.name)
            return 0;
          return (roleWithRightsVO1.name ?? '') < (roleWithRightsVO2.name ?? '') ? -1 : 1;
        }

        if(roleWithRightsVO1.platformRole === true && roleWithRightsVO2.platformRole === false){
          return -1;
        }
        return 1;
      });
  }

  onSubmitRole(event: RoleWithRightsDto){
    if(this.operation == 'duplicate' || this.operation == 'new') {
      this.roleConfigurationService.saveRoleWithCompanyType(event)
        .subscribe({
          next: () => {
            this.roleFetchSubject.next({ operation: 'new', roleUuid: undefined });
          },
          error: () => {
            this.roleFetchSubject.next({ operation: 'new', roleUuid: undefined });
          }
        });
    }
    if(this.operation == 'edit'){
      this.roleConfigurationService.updateRole(event)
        .subscribe({
          next: () => {
            this.showSavedDialog = true;
            this.navigateToAddPage()
          },
          error: () => {
            this.roleFetchSubject.next({ operation: 'edit', roleUuid: event.uuid });
          }
        });
    }
  }

  ngOnDestroy(){
    this.destroy$.next();
  }

  onEdit(roleUuid: string){
      this.router.navigate(['edit', roleUuid], {relativeTo: this.route.parent});
  }

  onDuplicate(roleUuid: string){
    this.router.navigate(['duplicate', roleUuid], {relativeTo: this.route.parent});
  }

  onDelete(roleUuid: string){
    this.roleService.remove(roleUuid).subscribe(() => {
      this.navigateToAddPage();
    })
  }

  navigateToAddPage(){
    this.router.navigate(['new'], {relativeTo: this.route.parent});
  }

  private setCompanyTypeSelectItems(){
    if(this.meta && this.meta.companyTypes){
      this.companyTypesSelectItems = this.meta.companyTypes.map((companyType: any) => {
        return {value: companyType,
          label: this.companyTypeTranslationMap[companyType] ?? companyType
        };
      })
    }

  }
}
